import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportPaymentByNewVsExistingUsersData,
} from '@src/types/reports';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import {
  exportPaymentsByNewVsExistingUsers,
  getPaymentsByNewVsExistingUsers,
} from '@src/services/reports';
import { buildCommonPayloadWithGrouping } from '@src/utils/buildCommonPayload';
import { toast } from 'react-toastify';
import { downloadFile } from '@src/lib/utils';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';
import { useAuthStore } from '@src/stores/authStore';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

const chartLabelMap: Record<string, string> = {
  total: 'Total Payments',
  existing_customers: 'Existing Customers',
  new_customers: 'New Customers',
  total_previous_period: 'Total (Previous Period)',
  existing_customers_previous_period: 'Existing Customers (Previous Period)',
  new_customers_previous_period: 'New Customers (Previous Period)',
  total_previous_year: 'Total (Previous Year)',
  existing_customers_previous_year: 'Existing Customers (Previous Year)',
  new_customers_previous_year: 'New Customers (Previous Year)',
};

export const PaymentsByNewVsExistingUsers: FC = () => {
  const { auth } = useAuthStore();
  const [data, setData] = useState<TReportPaymentByNewVsExistingUsersData[]>(
    []
  );

  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
  });

  const { columns, displayItems, tableData } =
    useReportTableColumns<TReportPaymentByNewVsExistingUsersData>({
      data,
      selectedItems: [],
      compareToPreviousPeriod: filters.compareToPreviousPeriod,
      compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
      getAccessField: (item) => item.date,
      getMainDataField: (item, key) => item.payments[key] || 0,
      getPreviousPeriodField: (item, key) => item.payments_previous?.[key] || 0,
      getPreviousYearField: (item, key) =>
        item.payments_previous_year?.[key] || 0,
      itemsField: 'payments',
      columnTitleFormatter: (key) => {
        switch (key) {
          case 'total':
            return 'PAYMENTS TOTAL';
          case 'existing_customers':
            return 'PAYMENTS FROM EXISTING CUSTOMERS';
          case 'new_customers':
            return 'PAYMENTS FROM NEW CUSTOMERS';
          default:
            return key.toUpperCase().replace('_', ' ');
        }
      },
      valueFormatter: (_, value) => `$${value.toLocaleString()}`,
      includeTotalsRow: true,
    });

  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.payments[key] || 0,
    getPreviousPeriodField: (item, key) => item.payments_previous?.[key],
    getPreviousYearField: (item, key) => item.payments_previous_year?.[key],
  });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildCommonPayloadWithGrouping(filters);
      const response = await getPaymentsByNewVsExistingUsers(payload);

      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to generate report. Please try again.');
    } finally {
      filters.setIsLoading(false);
    }
  };

  const exportReport = async () => {
    try {
      const payload = buildCommonPayloadWithGrouping(filters);
      const response = await exportPaymentsByNewVsExistingUsers(payload);

      downloadFile(response.data, 'payments-by-new-vs-existing-users');
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report. Please try again.');
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Payment By New vs Existing Users Report
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          showGroupByFilter
          showPeriodFilter
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #3D3D3D',
                  borderRadius: '8px',
                  boxShadow: '0px 7px 13px 0px #B6D72C14',
                }}
                formatter={(value: number, name: string) => [
                  `$${value.toLocaleString()}`,
                  chartLabelMap[name] || name,
                ]}
              />
              <Legend
                formatter={(value) => chartLabelMap[value] || value}
                wrapperStyle={{
                  paddingTop: '20px',
                }}
              />
              {displayItems.map((payment, index) => (
                <Line
                  key={payment}
                  activeDot={{ r: 6 }}
                  dataKey={payment}
                  dot={{ r: 4 }}
                  name={chartLabelMap[payment] || payment}
                  stroke={getChartColor(index)}
                  strokeWidth={2}
                  type="monotone"
                />
              ))}
              {filters.compareToPreviousPeriod &&
                displayItems.map((payment, index) => (
                  <Line
                    key={`${payment}_previous_period`}
                    dataKey={`${payment}_previous_period`}
                    dot={{ r: 3 }}
                    name={
                      chartLabelMap[`${payment}_previous_period`] ||
                      `${chartLabelMap[payment] || payment} (Previous Period)`
                    }
                    stroke={getChartColor(index)}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    type="monotone"
                  />
                ))}
              {filters.compareToSamePeriodPreviousYear &&
                displayItems.map((payment, index) => (
                  <Line
                    key={`${payment}_previous_year`}
                    dataKey={`${payment}_previous_year`}
                    dot={{ r: 3 }}
                    name={
                      chartLabelMap[`${payment}_previous_year`] ||
                      `${chartLabelMap[payment] || payment} (Previous Year)`
                    }
                    stroke={getChartColor(index)}
                    strokeDasharray="2 2"
                    strokeWidth={2}
                    type="monotone"
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          {canAccess(
            UserRolePermission.EXPORT_REPORTS,
            auth?.user?.permissions
          ) && (
            <div className="flex justify-end">
              <Button
                className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                variant="outline"
                onClick={exportReport}
              >
                Download Excel
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
